import * as React from 'react'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { adminService } from '../../../../core/services'
import { InputText, InputTextArea, ListItem, Select, Toggle } from '../../../atoms'
import AdminLayoutEdit from '../_layouts/edit'
import * as interfaces from '../../../../static/interfaces'
import ReactSelect from 'react-select'

declare var window: interfaces.IWindow

interface IProps {
  feature: IJsonResponse
  tags: IJsonResponse
  categories: IJsonResponse
  areas: IJsonResponse
}

const FIELDS = {
  id: 'id',
  slug: 'slug',
  is_parent: 'is_parent',
  feature_type: 'feature_type',
  title: 'title',
  detail_title: 'detail_title',
  description: 'description',
  detail_description: 'detail_description',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('feature', { scope: 'activerecord.models' }),
})

const convertArrayToOptions = array => {
  return array.map(item => {
    return {
      value: String(item.attributes?.id),
      label: item.attributes?.name,
    }
  })
}

const AdminFeatureEdit: React.FC<IProps> = props => {
  const { data: feature } = adminService.getDataFromJson(props.feature)
  const areaOptions = convertArrayToOptions(props?.areas?.data ?? [])
  const categoryOptions = convertArrayToOptions(props?.categories?.data ?? [])
  const tagOptions = convertArrayToOptions(props?.tags?.data ?? [])

  const areaDefaultValue =
    feature?.area_ids?.map(id => {
      return areaOptions.find(option => option.value === String(id))
    }) ?? []
  const categoryDefaultValue =
    feature?.category_ids?.map(id => {
      return categoryOptions.find(option => option.value === String(id))
    }) ?? []
  const tagDefaultValue =
    feature?.tag_ids?.map(id => {
      return tagOptions.find(option => option.value === String(id))
    }) ?? []
  const [areas, setAreas] = React.useState(areaDefaultValue)
  const [categories, setCategories] = React.useState(categoryDefaultValue)
  const [tags, setTags] = React.useState(tagDefaultValue)

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const formValues = {
        ...values,
        area_ids: areas.map(({ value }) => value),
        category_ids: categories.map(({ value }) => value),
        tag_ids: tags.map(({ value }) => value),
      }
      const formData = new FormData()
      Object.keys(formValues).forEach(key => {
        formData.append(`feature[${key}]`, values[key])
      })
      const { updatedFeature, flush } = await adminService.updateFeature(feature.id, formValues)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/admin/features'
    },
    [areas, categories, tags]
  )

  return (
    <AdminLayoutEdit
      model="feature"
      title={title}
      fields={FIELDS}
      indexLink="/admin/features"
      formItems={
        <>
          <ListItem>
            <InputText
              readonly={true}
              name="id"
              defaultValue={feature.id}
              label={I18n.t('generic.id')}
            />
          </ListItem>
          <ListItem>
            <InputText
              required={true}
              name="slug"
              defaultValue={feature.slug}
              label={I18n.t('generic.slug')}
            />
          </ListItem>
          <ListItem>
            <Toggle name="is_parent" label="Is parent" defaultChecked={feature.is_parent} />
          </ListItem>
          <ListItem>
            <Select
              required={true}
              defaultValue={feature.feature_type}
              name="feature_type"
              label="Feature type"
              options={[
                { value: 'area', label: 'area' },
                { value: 'category', label: 'category' },
              ]}
            />
          </ListItem>

          <ListItem>
            <InputText
              required={true}
              name="title"
              defaultValue={feature.title}
              label={I18n.t('generic.title')}
            />
          </ListItem>
          <ListItem>
            <InputTextArea
              required={true}
              name="description"
              defaultValue={feature.description}
              label={I18n.t('generic.description')}
            />
          </ListItem>
          <ListItem>
            <InputText
              required={true}
              name="detail_title"
              defaultValue={feature.detail_title}
              label={I18n.t('generic.detail_title')}
            />
          </ListItem>
          <ListItem>
            <InputTextArea
              required={true}
              name="detail_description"
              defaultValue={feature.detail_description}
              label={I18n.t('generic.detail_description')}
            />
          </ListItem>
          <ListItem>
            <ReactSelect
              options={areaOptions}
              isMulti={true}
              defaultValue={areaDefaultValue}
              title={I18n.t('generic.areas')}
              onChange={setAreas}
              menuPortalTarget={document.querySelector('body')}
            />
          </ListItem>
          <ListItem>
            <ReactSelect
              options={categoryOptions}
              isMulti={true}
              defaultValue={categoryDefaultValue}
              title={I18n.t('generic.categories')}
              onChange={setCategories}
              menuPortalTarget={document.querySelector('body')}
            />
          </ListItem>
          <ListItem>
            <ReactSelect
              options={tagOptions}
              isMulti={true}
              defaultValue={tagDefaultValue}
              title={I18n.t('generic.tags')}
              onChange={setTags}
              menuPortalTarget={document.querySelector('body')}
            />
          </ListItem>
        </>
      }
      handleSubmit={handleSubmit}
    />
  )
}

export default AdminFeatureEdit
